import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, Clock, XCircle, Home } from 'lucide-react';

const MAX_POLLS = 8;

const PaymentSuccessPage = () => {
  const [params] = useSearchParams();
  const ref = params.get('ref') || '';
  const [status, setStatus] = useState('loading'); // loading | success | pending | cancelled | error
  const [txn, setTxn] = useState(null);
  const [polls, setPolls] = useState(0);

  const fetchStatus = useCallback(async () => {
    if (!ref) {
      setStatus('error');
      return;
    }
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/payment/status/${ref}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error('not_found');
      const data = await r.json();
      setTxn(data);
      if (data.status === 'success') setStatus('success');
      else if (data.status === 'cancelled') setStatus('cancelled');
      else setStatus('pending');
    } catch {
      setStatus('error');
    }
  }, [ref]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (status !== 'pending' || polls >= MAX_POLLS) return;
    const t = setTimeout(() => {
      setPolls((p) => p + 1);
      fetchStatus();
    }, 2000);
    return () => clearTimeout(t);
  }, [status, polls, fetchStatus]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-cream flex items-center justify-center p-4">
      <Card className="w-full max-w-lg p-10 bg-white shadow-2xl text-center" data-testid="payment-success-card">
        {status === 'loading' && (
          <div className="space-y-4">
            <Loader2 className="animate-spin text-burgundy mx-auto" size={48} />
            <p className="text-slate-600">Vérification du paiement...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-4">
            <div className="inline-flex p-4 bg-green-100 rounded-full">
              <CheckCircle2 className="text-green-600" size={56} />
            </div>
            <h1 className="text-3xl font-bold text-burgundy">Paiement confirmé !</h1>
            <p className="text-slate-600">
              Merci, votre paiement a bien été reçu. Notre équipe vous contactera pour la livraison.
            </p>
            {txn && (
              <div className="text-sm bg-slate-50 rounded-lg p-4 text-left space-y-1 font-mono">
                <div><span className="text-slate-500">Référence:</span> {txn.ref_command}</div>
                <div><span className="text-slate-500">Montant:</span> {Number(txn.amount).toLocaleString('fr-FR')} F CFA</div>
                {txn.payment_method && (
                  <div><span className="text-slate-500">Méthode:</span> {txn.payment_method}</div>
                )}
              </div>
            )}
          </div>
        )}

        {status === 'pending' && (
          <div className="space-y-4">
            <Clock className="text-amber-500 mx-auto animate-pulse" size={56} />
            <h1 className="text-2xl font-bold text-slate-800">Paiement en attente de confirmation</h1>
            <p className="text-slate-600">
              Nous attendons la notification de PayTech... ({polls + 1}/{MAX_POLLS + 1})
            </p>
            {polls >= MAX_POLLS && (
              <p className="text-sm text-slate-500">
                Si vous avez bien payé, le statut sera mis à jour dans quelques minutes. Vous pouvez fermer cette page.
              </p>
            )}
          </div>
        )}

        {status === 'cancelled' && (
          <div className="space-y-4">
            <XCircle className="text-red-500 mx-auto" size={56} />
            <h1 className="text-2xl font-bold text-slate-800">Paiement annulé</h1>
            <p className="text-slate-600">Le paiement a été annulé. Vous pouvez réessayer si vous le souhaitez.</p>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-4">
            <XCircle className="text-red-500 mx-auto" size={56} />
            <h1 className="text-2xl font-bold text-slate-800">Référence introuvable</h1>
            <p className="text-slate-600">Nous n'avons pas trouvé cette transaction.</p>
          </div>
        )}

        <Link to="/" className="block mt-8">
          <Button variant="outline" size="lg" className="w-full" data-testid="back-home-btn">
            <Home size={18} className="mr-2" />
            Retour à l'accueil
          </Button>
        </Link>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
